import { useState, useEffect, useCallback } from 'react';
import { validateMediaImageUrl } from '../utils/imageLoader';

export interface UseImageLoaderOptions {
    onLoadSuccess?: (url: string) => void;
    onLoadError?: (error: Error) => void;
}

export interface UseImageLoaderResult {
    imageUrl: string;
    isLoading: boolean;
    hasError: boolean;
}

export const useImageLoader = (
    imagePath: string | null | undefined,
    options: UseImageLoaderOptions = {}
): UseImageLoaderResult => {
    const { onLoadSuccess, onLoadError } = options;

    const [imageUrl, setImageUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    const resolveImageUrl = useCallback((path: string): void => {
        if (!path) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const loadedUrl = validateMediaImageUrl(path);
            setImageUrl(loadedUrl);
            setIsLoading(false);
            onLoadSuccess?.(loadedUrl);
        } catch (err) {
            const loadError = err instanceof Error ? err : new Error(`Failed to load image: ${path}`);
            setIsLoading(false);
            setError(loadError);
            onLoadError?.(loadError);
            console.error(`Failed to load image: ${path}`, loadError);
        }
    }, [onLoadSuccess, onLoadError]);

    // Load image when path changes
    useEffect(() => {
        if (!imagePath) {
            setImageUrl('');
            setIsLoading(false);
            setError(null);
            return;
        }

        resolveImageUrl(imagePath);
    }, [imagePath, resolveImageUrl]);

    return {
        imageUrl,
        isLoading,
        hasError: error !== null
    };
};
